/**
 * Gobodo Process Builder Flow Service
 * CRUD for ProcessBuilderFlow__c. Full definition in DefinitionJson__c; scalars for list.
 */

import type {
  Process,
  ProcessCriteriaGroup,
  ProcessAction,
  ScheduledAction,
  ProcessSummary,
  ProcessBuilderStats,
  CreateProcessPayload,
  UpdateProcessPayload,
  CloneProcessPayload
} from '../../types/processBuilder';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord
} from './gobodoRecordService';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { getCustomSuffix } from '../../config/gobodoConfig';

const getObjectType = () => `ProcessBuilderFlow${getCustomSuffix()}`;

const FIELD = {
  NAME: 'Name__c',
  DESCRIPTION: 'Description__c',
  OBJECT_TYPE: 'ObjectType__c',
  TRIGGER_TYPE: 'TriggerType__c',
  IS_ACTIVE: 'IsActive__c',
  STATUS: 'Status__c',
  API_NAME: 'ApiName__c',
  CATEGORY: 'Category__c',
  DEFINITION_JSON: 'DefinitionJson__c',
  CREATED_DATE: 'CreatedDate__c',
  LAST_MODIFIED: 'LastModified__c'
} as const;

export async function ensureProcessBuilderFlowObjectExists(): Promise<void> {
  await ensureObjectTypeExists(getObjectType(), {
    label: 'Process Builder Flow',
    pluralLabel: 'Process Builder Flows',
    description: 'Process definition, nodes, connections, entry criteria.',
    icon: 'GitBranch',
    category: 'custom',
    isCustom: true,
    isActive: true
  });
}

function processToRecord(p: Partial<Process>): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [FIELD.NAME]: p.name ?? '',
    [FIELD.DESCRIPTION]: p.description ?? '',
    [FIELD.OBJECT_TYPE]: p.objectType ?? '',
    [FIELD.TRIGGER_TYPE]: p.triggerType ?? 'record_created_or_updated',
    [FIELD.IS_ACTIVE]: p.isActive ?? false,
    [FIELD.STATUS]: p.status ?? 'draft',
    [FIELD.API_NAME]: p.apiName ?? '',
    [FIELD.CATEGORY]: p.category ?? ''
  };
  if (p.createdDate) record[FIELD.CREATED_DATE] = p.createdDate;
  if (p.lastModifiedDate) record[FIELD.LAST_MODIFIED] = p.lastModifiedDate;
  record[FIELD.DEFINITION_JSON] = JSON.stringify({
    entryCriteria: p.entryCriteria,
    reevaluateOnUpdate: p.reevaluateOnUpdate,
    criteriaGroups: p.criteriaGroups,
    immediateActions: p.immediateActions,
    scheduledActions: p.scheduledActions,
    currentVersion: p.currentVersion,
    versions: p.versions,
    createdBy: p.createdBy,
    lastModifiedBy: p.lastModifiedBy,
    executionCount: p.executionCount,
    successCount: p.successCount,
    failureCount: p.failureCount
  });
  return record;
}

function recordToProcess(record: Record<string, any>): Process {
  const id = record.id != null ? String(record.id) : '';
  let def: Record<string, any> = {};
  try {
    const raw = record[FIELD.DEFINITION_JSON];
    if (raw && typeof raw === 'string') def = JSON.parse(raw);
    else if (raw && typeof raw === 'object') def = raw;
  } catch {
    // keep default
  }
  return {
    id,
    name: record[FIELD.NAME] ?? '',
    description: record[FIELD.DESCRIPTION] ?? '',
    apiName: record[FIELD.API_NAME] ?? undefined,
    objectType: record[FIELD.OBJECT_TYPE] ?? '',
    triggerType: (record[FIELD.TRIGGER_TYPE] as Process['triggerType']) ?? 'record_created_or_updated',
    isActive: Boolean(record[FIELD.IS_ACTIVE]),
    status: (record[FIELD.STATUS] as Process['status']) ?? 'draft',
    category: record[FIELD.CATEGORY] ?? undefined,
    entryCriteria: def.entryCriteria,
    reevaluateOnUpdate: def.reevaluateOnUpdate,
    criteriaGroups: def.criteriaGroups ?? [],
    immediateActions: def.immediateActions ?? {},
    scheduledActions: def.scheduledActions ?? {},
    currentVersion: def.currentVersion ?? 1,
    versions: def.versions ?? [],
    createdDate: record[FIELD.CREATED_DATE] ?? new Date().toISOString(),
    createdBy: def.createdBy ?? 'current_user',
    lastModifiedDate: record[FIELD.LAST_MODIFIED] ?? new Date().toISOString(),
    lastModifiedBy: def.lastModifiedBy ?? 'current_user',
    executionCount: def.executionCount,
    successCount: def.successCount,
    failureCount: def.failureCount
  };
}

function toSummary(p: Process): ProcessSummary {
  const actionCount = Object.values(p.immediateActions || {}).reduce((s, a) => s + a.length, 0);
  const scheduledActionCount = Object.values(p.scheduledActions || {}).reduce((s, sa) => s + sa.length, 0);
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    objectType: p.objectType,
    triggerType: p.triggerType,
    isActive: p.isActive,
    status: p.status,
    category: p.category,
    criteriaGroupCount: (p.criteriaGroups || []).length,
    actionCount,
    scheduledActionCount,
    currentVersion: p.currentVersion ?? 1,
    lastModifiedDate: p.lastModifiedDate,
    lastModifiedByName: p.lastModifiedByName,
    executionCount: p.executionCount,
    successRate:
      p.executionCount && p.executionCount > 0 && p.successCount != null
        ? Math.round((p.successCount / p.executionCount) * 100)
        : undefined
  };
}

export async function getProcesses(): Promise<Process[]> {
  await ensureProcessBuilderFlowObjectExists();
  const { records } = await getGobodoRecords(getObjectType());
  return records.map((r: Record<string, any>) => recordToProcess(r));
}

export async function getProcessById(id: string): Promise<Process | null> {
  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return recordToProcess(record);
}

export async function getProcessSummaries(): Promise<ProcessSummary[]> {
  const processes = await getProcesses();
  return processes.map(toSummary);
}

export async function getStats(): Promise<ProcessBuilderStats> {
  const processes = await getProcesses();
  const active = processes.filter(p => p.isActive).length;
  const inactive = processes.filter(p => !p.isActive && p.status === 'inactive').length;
  const draft = processes.filter(p => p.status === 'draft').length;
  const totalExec = processes.reduce((s, p) => s + (p.executionCount || 0), 0);
  const successExec = processes.reduce((s, p) => s + (p.successCount || 0), 0);
  const failedExec = processes.reduce((s, p) => s + (p.failureCount || 0), 0);
  const byObjectType: Record<string, number> = {};
  const byTriggerType: Record<string, number> = {};
  for (const p of processes) {
    byObjectType[p.objectType] = (byObjectType[p.objectType] || 0) + 1;
    byTriggerType[p.triggerType] = (byTriggerType[p.triggerType] || 0) + 1;
  }
  return {
    totalProcesses: processes.length,
    activeProcesses: active,
    inactiveProcesses: inactive,
    draftProcesses: draft,
    totalExecutions: totalExec,
    successfulExecutions: successExec,
    failedExecutions: failedExec,
    executionsToday: 0,
    successRate: totalExec > 0 ? Math.round((successExec / totalExec) * 100) : 0,
    avgExecutionTime: 0,
    byObjectType,
    byTriggerType: byTriggerType as ProcessBuilderStats['byTriggerType']
  };
}

const generateId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export async function createProcess(payload: CreateProcessPayload): Promise<Process> {
  const now = new Date().toISOString();
  const process: Partial<Process> = {
    ...payload,
    isActive: payload.isActive ?? false,
    status: payload.isActive ? 'active' : 'draft',
    criteriaGroups: (payload.criteriaGroups ?? []) as ProcessCriteriaGroup[],
    immediateActions: (payload.immediateActions ?? {}) as Record<string, ProcessAction[]>,
    scheduledActions: (payload.scheduledActions ?? {}) as Record<string, ScheduledAction[]>,
    entryCriteria: payload.entryCriteria as ProcessCriteriaGroup | undefined,
    currentVersion: 1,
    versions: [
      {
        id: generateId('pv'),
        versionNumber: 1,
        status: 'draft',
        createdDate: now,
        createdBy: 'current_user',
        description: 'Initial version'
      }
    ],
    createdDate: now,
    createdBy: 'current_user',
    lastModifiedDate: now,
    lastModifiedBy: 'current_user'
  };
  const created = await createGobodoRecord(getObjectType(), processToRecord(process));
  return recordToProcess(created);
}

export async function updateProcess(payload: UpdateProcessPayload): Promise<Process | null> {
  const existing = await getProcessById(payload.id);
  if (!existing) return null;
  const merged = { ...existing, ...payload } as Process;
  const updated = await updateGobodoRecord(payload.id, processToRecord(merged));
  return recordToProcess(updated);
}

export async function deleteProcess(id: string): Promise<boolean> {
  await deleteGobodoRecord(id);
  return true;
}

export async function cloneProcess(payload: CloneProcessPayload): Promise<Process | null> {
  const original = await getProcessById(payload.sourceProcessId);
  if (!original) return null;
  const createPayload: CreateProcessPayload = {
    name: payload.newName,
    description: payload.description ?? `Clone of ${original.name}`,
    objectType: original.objectType,
    triggerType: original.triggerType,
    isActive: false,
    category: original.category,
    entryCriteria: original.entryCriteria,
    reevaluateOnUpdate: original.reevaluateOnUpdate,
    criteriaGroups: original.criteriaGroups.map(({ id: _i, ...r }) => r),
    immediateActions: Object.fromEntries(
      Object.entries(original.immediateActions || {}).map(([k, actions]) => [
        k,
        actions.map(({ id: _a, ...a }) => a)
      ])
    ),
    scheduledActions: payload.includeScheduledActions
      ? Object.fromEntries(
          Object.entries(original.scheduledActions || {}).map(([k, scheduled]) => [
            k,
            scheduled.map(({ id: _s, ...s }) => ({ ...s, actions: s.actions.map(({ id: _a, ...a }) => a) }))
          ])
        )
      : undefined
  };
  return createProcess(createPayload);
}

export async function activateProcess(id: string): Promise<Process | null> {
  const p = await getProcessById(id);
  if (!p) return null;
  return updateProcess({ id, isActive: true, status: 'active' });
}

export async function deactivateProcess(id: string): Promise<Process | null> {
  const p = await getProcessById(id);
  if (!p) return null;
  return updateProcess({ id, isActive: false, status: 'inactive' });
}
